import { useCollateralInputValidation } from '@ui/features/Trade/components/TradeModule/hooks/useCollateralInputValidation';
import { useTriggerPriceInputValidation } from '@ui/features/Trade/components/TradeModule/hooks/useTriggerPriceInputValidation';
import { useDirectCollateralOpenStatus } from '@ui/features/Trade/hooks/useDirectCollateralOpenStatus';
import { useEntryPriceAfterSpread } from '@ui/features/Trade/hooks/useEntryPriceAfterSpread';
import { useFromInput } from '@ui/features/Trade/hooks/useFromInput';
import { useGetExecutionFeeInPayToken } from '@ui/features/Trade/hooks/useGetExecutionFee';
import { useMarketStatus } from '@ui/features/Trade/hooks/useMarketStatus';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { useStopLossStore } from '@ui/features/Trade/store/useStopLossStore';
import { useTakeProfitStore } from '@ui/features/Trade/store/useTakeProfitStore';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { OrderType } from '@ui/types/trade';
import BigNumber from 'bignumber.js';
import { isEmpty, isNil } from 'lodash-es';
import { useEffect, useMemo } from 'react';

import { useCollateralToken } from '../../store/useCollateralToken';
import { useIsLong } from '../../store/useIsLong';
import { useOrderType } from '../../store/useOrderType';
import { useStopLossPriceValidation, useTakeProfitPriceValidation } from '../../TpSlModal/hooks/useTpSlValidation';
import { useOpenPositionFee } from '../useOpenPositionFee';
import { useConfirmBtnDisabledStore } from './useConfirmBtnDisabledStore';

export const useIsConfirmBtnDisabledUpdate = () => {
  const inputObject = useFromInput((s) => s.input);
  const inputUsd = inputObject.value.usd;
  const inputToken = inputObject.value.token;
  const inputTriggerPrice = inputObject.triggerPrice;

  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const payTokenPrice = usePayTokenPriceLatest(payToken);
  const directCollateralStatus = useDirectCollateralOpenStatus(payToken);
  const openPositionFee = useOpenPositionFee();
  const executionFeeInPayToken = useGetExecutionFeeInPayToken();
  const entryPrice = useEntryPriceAfterSpread();
  const orderType = useOrderType((s) => s._persistValue);
  const isLimitOrder = orderType !== OrderType.MARKET;
  const collateralAmountValidation = useCollateralInputValidation();
  const triggerPriceValidation = useTriggerPriceInputValidation();
  const isLong = useIsLong((s) => s._persistValue);
  const marketStatus = useMarketStatus();
  const tpValue = useTakeProfitStore((s) => s.tpValue);
  const slValue = useStopLossStore((s) => s.slValue);
  const confirmBtnDisabledStore = useConfirmBtnDisabledStore();

  const tpValidation = useTakeProfitPriceValidation(tpValue);
  const slValidation = useStopLossPriceValidation(slValue);
  const directCollateralCapRequiredAmount = useMemo(() => {
    if (!directCollateralStatus.isDirectCollateral || isNil(inputToken)) {
      return undefined;
    }

    if (isLimitOrder) {
      return inputToken;
    }

    if (isNil(openPositionFee) || isNil(executionFeeInPayToken)) {
      return undefined;
    }

    return BigNumber.maximum(
      inputToken
        .minus(openPositionFee.value.final)
        .minus(executionFeeInPayToken),
      ZERO_BIG_NUMBER,
    );
  }, [
    directCollateralStatus.isDirectCollateral,
    executionFeeInPayToken,
    inputToken,
    isLimitOrder,
    openPositionFee,
  ]);
  const isDirectCollateralOpenCapInsufficient =
    directCollateralStatus.isDirectCollateral &&
    !directCollateralStatus.data?.openCap.isUnlimited &&
    !isNil(directCollateralCapRequiredAmount) &&
    !isNil(directCollateralStatus.data?.openCap.remainingOpenAmount) &&
    directCollateralCapRequiredAmount.gt(
      directCollateralStatus.data.openCap.remainingOpenAmount,
    );

  const reason = (() => {
    if (
      isLimitOrder &&
      (isNil(inputTriggerPrice) || isEmpty(inputTriggerPrice))
    ) {
      return 'Enter trigger price';
    }
    if (isLimitOrder && !isNil(triggerPriceValidation.error)) {
      return 'Invalid trigger price';
    }
    if (isNil(inputUsd)) {
      return 'Enter margin';
    }
    if (isNil(entryPrice)) {
      return 'Try again';
    }
    if (directCollateralStatus.reason) {
      return directCollateralStatus.reason;
    }
    if (isDirectCollateralOpenCapInsufficient) {
      return 'Insufficient CAP';
    }
    if (isNil(payTokenPrice)) {
      return 'Price error';
    }
    if (!isNil(collateralAmountValidation?.error)) {
      return 'Invalid margin';
    }
    if (isLong && marketStatus.long.isDisabled) {
      return 'Long disabled';
    }
    if (!isLong && marketStatus.short.isDisabled) {
      return 'Short disabled';
    }
    if (!tpValidation.valid) {
      return 'Invalid TP';
    }
    if (!slValidation.valid) {
      return 'Invalid SL';
    }
    
    return null;
  })();

  const isConfirmBtnDisabled = Boolean(reason);

  useEffect(() => {
    if (
      confirmBtnDisabledStore.isDisabled == isConfirmBtnDisabled &&
      confirmBtnDisabledStore.reason == reason
    ) {
      return;
    }
    confirmBtnDisabledStore.setState({
      isDisabled: isConfirmBtnDisabled,
      reason,
    });
  }, [confirmBtnDisabledStore, isConfirmBtnDisabled, reason]);
};
